// Client for the Build Guide chat that helps customers decide what to build in EasyAI.
// Backend: POST /api/v2/build-guide/chat and /events in api/core/routers/build_guide.py.

import { ApiError, TokenGetter } from './learningApi';

/** The backend only reads the most recent turns; sending more just costs bandwidth. */
export const MAX_SENT_MESSAGES = 30;
export const MAX_MESSAGE_CHARS = 4000;

export type BuildGuideRole = 'user' | 'assistant';

export interface BuildGuideTurn {
  role: BuildGuideRole;
  content: string;
}

export interface BuildGuideAction {
  label: string;
  /** Portal path validated by the server against its allowlist. */
  path: string;
  description: string;
}

export type BuildGuideRecommendationKind = 'function' | 'chain' | 'chatbot' | 'studio' | 'other';

export interface BuildGuideRecommendation {
  kind: BuildGuideRecommendationKind;
  summary: string;
  why: string;
}

export interface BuildGuideReply {
  reply: string;
  quick_replies: string[];
  actions: BuildGuideAction[];
  recommendation: BuildGuideRecommendation | null;
}

export function buildGuideApi(getToken: TokenGetter) {
  return {
    chat: async (messages: BuildGuideTurn[], conversationId: string, signal?: AbortSignal): Promise<BuildGuideReply> => {
      const token = await getToken();
      const res = await fetch('/api/v2/build-guide/chat', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: messages
            .slice(-MAX_SENT_MESSAGES)
            .map((m) => ({ role: m.role, content: m.content.slice(0, MAX_MESSAGE_CHARS) })),
          conversation_id: conversationId,
        }),
        signal,
      });
      if (!res.ok) {
        let message = res.statusText;
        try {
          const body = await res.json();
          message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
        } catch {
          /* keep statusText */
        }
        throw new ApiError(message, res.status);
      }
      const data = (await res.json()) as Partial<BuildGuideReply>;
      return {
        reply: data.reply || '',
        quick_replies: Array.isArray(data.quick_replies) ? data.quick_replies : [],
        actions: Array.isArray(data.actions) ? data.actions : [],
        recommendation: data.recommendation ?? null,
      };
    },

    /**
     * Records a click on a guide link for the admin funnel. Never throws. Prefill params hold the
     * user's own text, so only the route path and `view` (which picks the destination) are sent.
     */
    trackAction: (conversationId: string, path: string): void => {
      const [pathname, query = ''] = path.split('#')[0].split('?');
      const view = new URLSearchParams(query).get('view');
      const tracked = view ? `${pathname}?view=${encodeURIComponent(view)}` : pathname;
      void (async () => {
        try {
          const token = await getToken();
          await fetch('/api/v2/build-guide/events', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ conversation_id: conversationId, type: 'action_clicked', path: tracked }),
            keepalive: true,
          });
        } catch {
          /* tracking is best-effort */
        }
      })();
    },
  };
}
